import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCarrinho } from '../../context/CarrinhoContext';
import { useClienteAuth } from '../../context/ClienteAuthContext';
import { formatBRL } from '../../components/shared';
import api from '../../services/api';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
const MAX_HOME = 8; // produtos por departamento na home

const fmtQtd = (produto, qtdVal) => {
  if (produto.passo_gramas && produto.unidade_medida === 'KG') {
    const g = Math.round(qtdVal * 1000);
    return g < 1000 ? `${g}g` : `${(g / 1000).toFixed(3).replace('.', ',')} kg`;
  }
  return qtdVal;
};

// ── Carrossel de Banners ─────────────────────────────────────
function BannerCarrossel({ banners }) {
  const [idx, setIdx] = useState(0);
  const pausadoRef    = useRef(false);
  const timerRef      = useRef(null);

  const avancar = useCallback(() => setIdx(i => (i + 1) % banners.length), [banners.length]);

  useEffect(() => {
    if (banners.length <= 1) return;
    timerRef.current = setInterval(() => { if (!pausadoRef.current) avancar(); }, 5000);
    return () => clearInterval(timerRef.current);
  }, [banners.length, avancar]);

  if (!banners.length) return null;

  const ir = (i) => { setIdx(i); clearInterval(timerRef.current); timerRef.current = setInterval(() => { if (!pausadoRef.current) avancar(); }, 5000); };

  return (
    <div
      style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', marginTop: 24, marginBottom: 24, userSelect: 'none' }}
      onMouseEnter={() => { pausadoRef.current = true; }}
      onMouseLeave={() => { pausadoRef.current = false; }}
    >
      {/* Faixa deslizante */}
      <div style={{ display: 'flex', transition: 'transform .5s cubic-bezier(.4,0,.2,1)', transform: `translateX(-${idx * 100}%)` }}>
        {banners.map((b, i) => (
          <img key={b.id} src={`${API}${b.url}`} alt={`Banner ${i + 1}`}
            style={{ width: '100%', flexShrink: 0, maxHeight: 320, objectFit: 'cover', display: 'block' }} />
        ))}
      </div>

      {/* Setas — só aparecem com mais de 1 banner */}
      {banners.length > 1 && (
        <>
          <button onClick={() => ir((idx - 1 + banners.length) % banners.length)}
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,.45)', border: 'none', borderRadius: '50%', width: 36, height: 36, color: '#fff', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
            ‹
          </button>
          <button onClick={() => ir((idx + 1) % banners.length)}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,.45)', border: 'none', borderRadius: '50%', width: 36, height: 36, color: '#fff', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
            ›
          </button>

          {/* Dots */}
          <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
            {banners.map((_, i) => (
              <button key={i} onClick={() => ir(i)}
                style={{ width: i === idx ? 22 : 8, height: 8, borderRadius: 99, border: 'none', padding: 0, background: i === idx ? '#FF6B35' : 'rgba(255,255,255,.65)', cursor: 'pointer', transition: 'all .3s' }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Rodapé ───────────────────────────────────────────────────
function Rodape({ empresa }) {
  return (
    <footer style={{ background: '#1A1A2E', color: '#9090aa', marginTop: 48, padding: '40px 20px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 32, marginBottom: 32 }}>
          <div>
            {empresa?.logo_url
              ? <img src={`${API}${empresa.logo_url}`} alt="logo" style={{ height: 48, borderRadius: 8, marginBottom: 12, objectFit: 'contain' }} />
              : <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', marginBottom: 12 }}>🍽 {empresa?.nome_fantasia}</div>
            }
            {empresa?.telefone && <div style={{ fontSize: '.85rem', marginBottom: 6 }}>📞 {empresa.telefone}</div>}
            {empresa?.email    && <div style={{ fontSize: '.85rem', marginBottom: 6 }}>✉️ {empresa.email}</div>}
            {empresa?.endereco && (
              <div style={{ fontSize: '.85rem', lineHeight: 1.5 }}>
                📍 {empresa.endereco}{empresa.numero ? `, ${empresa.numero}` : ''}<br />
                {empresa.cidade}/{empresa.estado}
              </div>
            )}
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, marginBottom: 12, fontSize: '.9rem' }}>Links</div>
            {[
              { label: 'Início', to: '#topo' },
              { label: 'Termos de Uso', to: 'termos' },
              { label: 'Política de Privacidade', to: 'privacidade' },
              { label: 'Contato', to: 'contato' },
            ].map(l => (
              <div key={l.label} style={{ marginBottom: 8 }}>
                <Link to={l.to.startsWith('#') ? l.to : `/pagina/${l.to}`}
                  style={{ color: '#9090aa', fontSize: '.85rem', textDecoration: 'none' }}
                  onMouseOver={e => e.target.style.color = '#FF6B35'}
                  onMouseOut={e => e.target.style.color = '#9090aa'}>
                  {l.label}
                </Link>
              </div>
            ))}
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, marginBottom: 12, fontSize: '.9rem' }}>Atendimento</div>
            <div style={{ fontSize: '.85rem', lineHeight: 1.8 }}>
              Dúvidas sobre seu pedido?<br />Entre em contato conosco<br />
              {empresa?.telefone && (
                <a href={`https://wa.me/55${empresa.telefone.replace(/\D/g,'')}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ color: '#25D366', fontWeight: 600, textDecoration: 'none' }}>
                  💬 Falar no WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
        <div style={{ borderTop: '1px solid #2a2a45', paddingTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, fontSize: '.78rem' }}>
          <span>© {new Date().getFullYear()} {empresa?.nome_fantasia}. Todos os direitos reservados.</span>
          <span style={{ opacity: .5 }}>Powered by ArautoFood · GenPac Sistemas</span>
        </div>
      </div>
    </footer>
  );
}

// ── Modal de Produto ─────────────────────────────────────────
function ProdutoModal({ produto, onClose, onAdicionar, onRemover, quantidade, observacaoAtual, onAtualizarObservacao }) {
  const [nota, setNota] = useState('');

  useEffect(() => { setNota(observacaoAtual || ''); }, [produto?.id]); // eslint-disable-line

  if (!produto) return null;

  const salvarNota = () => {
    if (quantidade > 0) onAtualizarObservacao(produto.id, nota.trim() || null);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 300, display: 'flex', alignItems: 'flex-end', animation: 'fadeIn .15s' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 560, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto', animation: 'slideUp .25s' }} onClick={e => e.stopPropagation()}>
        {produto.foto_url
          ? <img src={`${API}${produto.foto_url}`} alt={produto.descricao} style={{ width: '100%', height: 240, objectFit: 'cover', borderRadius: '20px 20px 0 0' }} />
          : <div style={{ height: 160, background: '#f0ede8', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4rem' }}>🍽</div>
        }
        <div style={{ padding: '20px 24px 32px' }}>
          {produto.destaque && (
            <span style={{ fontSize: '.72rem', color: '#FF6B35', fontWeight: 700, background: '#fff1ec', padding: '2px 8px', borderRadius: 99, marginBottom: 8, display: 'inline-block' }}>
              ⭐ DESTAQUE
            </span>
          )}
          <h2 style={{ fontSize: '1.2rem', fontWeight: 800, margin: '8px 0' }}>{produto.descricao}</h2>
          {produto.descricao_detalhada && (
            <p style={{ color: '#666', fontSize: '.9rem', lineHeight: 1.6, marginBottom: 16 }}>{produto.descricao_detalhada}</p>
          )}
          <div style={{ marginTop: 16 }}>
            <label style={{ fontSize: '.8rem', fontWeight: 700, color: '#555', display: 'block', marginBottom: 6 }}>
              📝 Alguma observação? <span style={{ fontWeight: 400, color: '#aaa' }}>(opcional)</span>
            </label>
            <textarea value={nota} onChange={e => setNota(e.target.value)} onBlur={salvarNota}
              placeholder="Ex: sem cebola, ponto da carne bem passado..." rows={2} maxLength={200}
              style={{ width: '100%', border: '1.5px solid #eee', borderRadius: 10, padding: '10px 12px', fontSize: '.85rem', fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
            <div>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#FF6B35' }}>{formatBRL(produto.preco_promocional || produto.preco_venda)}</span>
              {produto.preco_promocional && (
                <div style={{ fontSize: '.85rem', color: '#999', textDecoration: 'line-through', lineHeight: 1 }}>{formatBRL(produto.preco_venda)}</div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {quantidade > 0 && (
                <>
                  <button onClick={() => onRemover(produto.id)} style={{ width: 40, height: 40, borderRadius: '50%', background: '#1A1A2E', color: '#fff', border: 'none', fontSize: '1.4rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontWeight: 800, fontSize: '1.1rem', minWidth: 24, textAlign: 'center' }}>{fmtQtd(produto, quantidade)}</span>
                </>
              )}
              <button onClick={() => { onAdicionar(produto, nota.trim() || null); toast.success('Adicionado!', { duration: 800 }); }}
                style={{ background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 24px', fontWeight: 700, fontSize: '.95rem', cursor: 'pointer' }}>
                {quantidade > 0 ? '+ Mais um' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Carrinho flutuante ───────────────────────────────────────
function CarrinhoFlutuante({ total, totalItens, onAbrir }) {
  if (totalItens === 0) return null;
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 200, width: 'calc(100% - 32px)', maxWidth: 480 }}>
      <button onClick={onAbrir} style={{ width: '100%', background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 16, padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 8px 32px rgba(255,107,53,.4)', fontFamily: 'inherit' }}>
        <span style={{ background: 'rgba(255,255,255,.25)', borderRadius: 8, padding: '4px 10px', fontWeight: 800, fontSize: '.9rem' }}>
          {totalItens} {totalItens === 1 ? 'item' : 'itens'}
        </span>
        <span style={{ fontWeight: 800, fontSize: '1rem' }}>🛒 Ver carrinho</span>
        <span style={{ fontWeight: 800, fontSize: '1rem' }}>{formatBRL(total)}</span>
      </button>
    </div>
  );
}

// ── Carrinho Drawer ──────────────────────────────────────────
function CarrinhoDrawer({ aberto, onFechar, itens, total, totalItens, onAdicionar, onRemover, onRemoverTudo, onAtualizarObservacao, empresaId }) {
  const navigate = useNavigate();
  const [editandoId, setEditandoId] = useState(null);
  const [textoTemp, setTextoTemp]   = useState('');

  const abrirEdicaoNota = (item) => { setEditandoId(item.id); setTextoTemp(item.observacao || ''); };
  const salvarNota = (id) => { onAtualizarObservacao(id, textoTemp.trim() || null); setEditandoId(null); };

  return (
    <>
      {aberto && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 199 }} onClick={onFechar} />}
      <div style={{ position: 'fixed', right: 0, top: 0, height: '100dvh', width: '100%', maxWidth: 400, background: '#fff', zIndex: 200, display: 'flex', flexDirection: 'column', transform: aberto ? 'translateX(0)' : 'translateX(100%)', transition: 'transform .3s cubic-bezier(.4,0,.2,1)', boxShadow: '-4px 0 32px rgba(0,0,0,.12)' }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontWeight: 800, fontSize: '1.1rem' }}>🛒 Carrinho</h2>
            <span style={{ fontSize: '.8rem', color: '#888' }}>{totalItens} {totalItens === 1 ? 'item' : 'itens'}</span>
          </div>
          <button onClick={onFechar} style={{ background: '#f5f5f5', border: 'none', borderRadius: 10, width: 36, height: 36, cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {itens.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#aaa' }}>
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>🛒</div>
              <div style={{ fontWeight: 600 }}>Carrinho vazio</div>
              <div style={{ fontSize: '.85rem', marginTop: 4 }}>Adicione produtos para continuar</div>
            </div>
          ) : itens.map(item => (
            <div key={item.id} style={{ display: 'flex', gap: 12, padding: 12, marginBottom: 8, background: '#fafaf8', borderRadius: 12, alignItems: 'center' }}>
              {item.foto_url
                ? <img src={`${API}${item.foto_url}`} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: 56, height: 56, borderRadius: 10, background: '#eee', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>🍽</div>
              }
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '.875rem', marginBottom: 2 }}>{item.descricao}</div>
                {editandoId === item.id ? (
                  <textarea autoFocus value={textoTemp} onChange={e => setTextoTemp(e.target.value)}
                    onBlur={() => salvarNota(item.id)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.target.blur(); } }}
                    placeholder="Ex: sem cebola..." rows={2} maxLength={200} onClick={e => e.stopPropagation()}
                    style={{ width: '100%', border: '1.5px solid #FF6B35', borderRadius: 8, padding: '6px 8px', fontSize: '.75rem', fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box', marginBottom: 4 }} />
                ) : item.observacao ? (
                  <button onClick={() => abrirEdicaoNota(item)} style={{ display: 'block', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '.75rem', color: '#888', fontStyle: 'italic', marginBottom: 2, textAlign: 'left' }}>
                    📝 {item.observacao} <span style={{ color: '#FF6B35', fontStyle: 'normal', fontWeight: 700 }}>✎ editar</span>
                  </button>
                ) : (
                  <button onClick={() => abrirEdicaoNota(item)} style={{ display: 'block', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '.75rem', color: '#FF6B35', fontWeight: 700, marginBottom: 2 }}>
                    + observação
                  </button>
                )}
                <div style={{ fontSize: '.8rem', color: '#FF6B35', fontWeight: 700 }}>{formatBRL(item.preco_venda)}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                  <button onClick={() => onRemover(item.id)} style={{ width: 28, height: 28, borderRadius: '50%', background: '#1A1A2E', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <span style={{ fontWeight: 800, minWidth: 20, textAlign: 'center' }}>
                    {item.passo_gramas && item.unidade_medida === 'KG' ? item.quantidade.toFixed(3).replace('.', ',') + ' kg' : item.quantidade}
                  </span>
                  <button onClick={() => onAdicionar(item)} style={{ width: 28, height: 28, borderRadius: '50%', background: '#FF6B35', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontWeight: 800, fontSize: '.9rem' }}>{formatBRL(item.preco_venda * item.quantidade)}</div>
                <button onClick={() => onRemoverTudo(item.id)} style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: '.75rem', marginTop: 4 }}>remover</button>
              </div>
            </div>
          ))}
        </div>
        {itens.length > 0 && (
          <div style={{ padding: '16px 20px', paddingBottom: 'max(20px, env(safe-area-inset-bottom))', borderTop: '1px solid #eee' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: '1rem', fontWeight: 700 }}>Total</span>
              <span style={{ fontSize: '1.2rem', fontWeight: 800, color: '#FF6B35' }}>{formatBRL(total)}</span>
            </div>
            <button onClick={() => { onFechar(); navigate(`/${empresaId}/checkout`); }}
              style={{ width: '100%', background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 14, padding: '15px', fontWeight: 800, fontSize: '1rem', cursor: 'pointer', fontFamily: 'inherit' }}>
              Finalizar Pedido →
            </button>
            <button onClick={onFechar} style={{ width: '100%', background: 'none', border: 'none', color: '#888', marginTop: 10, cursor: 'pointer', fontSize: '.875rem', fontFamily: 'inherit', padding: 8 }}>
              ← Continuar comprando
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Sidebar de Subcategorias ─────────────────────────────────
function SidebarSubcategorias({ categoria, subcatAtiva, onSelectSubcat }) {
  const subs = categoria?.subcategorias || [];

  return (
    <aside style={{ width: 220, flexShrink: 0 }}>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', overflow: 'hidden', position: 'sticky', top: 140 }}>
        <div style={{ background: '#FF6B35', color: '#fff', padding: '13px 16px', fontWeight: 700, fontSize: '.875rem' }}>
          ☰ Filtrar por setor
        </div>
        {subs.length === 0 ? (
          <div style={{ padding: '14px 16px', fontSize: '.82rem', color: '#aaa', fontStyle: 'italic' }}>
            Sem subcategoria
          </div>
        ) : (
          <>
            <button onClick={() => onSelectSubcat(null)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '11px 16px', background: subcatAtiva === null ? '#fff8f5' : 'transparent', border: 'none', borderBottom: '1px solid #f5f5f5', borderLeft: subcatAtiva === null ? '3px solid #FF6B35' : '3px solid transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', color: subcatAtiva === null ? '#FF6B35' : '#444', fontWeight: subcatAtiva === null ? 700 : 400, fontSize: '.85rem' }}>
              <span>Todos</span>
              <span style={{ fontSize: '.72rem', color: '#999', background: '#f0f0f0', padding: '2px 8px', borderRadius: 99 }}>{categoria?.produtos.length ?? 0}</span>
            </button>
            {subs.map((sub, idx) => {
              const count = (categoria?.produtos || []).filter(p => p.subcategoria_id === sub.id).length;
              return (
                <button key={sub.id} onClick={() => onSelectSubcat(sub.id)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '11px 16px', background: subcatAtiva === sub.id ? '#fff8f5' : 'transparent', border: 'none', borderBottom: idx < subs.length - 1 ? '1px solid #f5f5f5' : 'none', borderLeft: subcatAtiva === sub.id ? '3px solid #FF6B35' : '3px solid transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', color: subcatAtiva === sub.id ? '#FF6B35' : '#444', fontWeight: subcatAtiva === sub.id ? 700 : 400, fontSize: '.85rem', transition: 'all .15s' }}
                  onMouseOver={e => { if (subcatAtiva !== sub.id) e.currentTarget.style.background = '#fafafa'; }}
                  onMouseOut={e => { if (subcatAtiva !== sub.id) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.descricao}</span>
                  <span style={{ fontSize: '.72rem', color: '#999', background: '#f0f0f0', padding: '2px 8px', borderRadius: 99, flexShrink: 0, marginLeft: 8 }}>{count}</span>
                </button>
              );
            })}
          </>
        )}
      </div>
    </aside>
  );
}

// ── Card de Produto ──────────────────────────────────────────
function ProdutoCard({ p, qtdAtual, onAdicionar, onRemover, onAbrirModal, imageHeight = 150 }) {
  return (
    <div onClick={() => onAbrirModal(p)}
      style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1px solid #eee', cursor: 'pointer', transition: 'box-shadow .2s, transform .2s', position: 'relative', display: 'flex', flexDirection: 'column' }}
      onMouseOver={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseOut={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
      {p.foto_url
        ? <img src={`${API}${p.foto_url}`} alt={p.descricao} loading="lazy" style={{ width: '100%', height: imageHeight, objectFit: 'cover' }} />
        : <div style={{ height: imageHeight, background: '#f5f2ee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>🍽</div>
      }
      {p.destaque && (
        <span style={{ position: 'absolute', top: 8, left: 8, background: '#FF6B35', color: '#fff', fontSize: '.65rem', fontWeight: 800, padding: '2px 8px', borderRadius: 99 }}>⭐ Destaque</span>
      )}
      <div style={{ padding: '10px 12px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: 700, fontSize: '.875rem', marginBottom: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.descricao}</div>
        {p.descricao_detalhada && (
          <div style={{ fontSize: '.75rem', color: '#999', marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.descricao_detalhada}</div>
        )}
        <div style={{ marginTop: 'auto', paddingTop: 6 }}>
          <span style={{ fontWeight: 800, color: '#FF6B35', fontSize: '.95rem' }}>{formatBRL(p.preco_promocional || p.preco_venda)}</span>
          {p.preco_promocional && (
            <div style={{ fontSize: '.72rem', color: '#999', textDecoration: 'line-through', lineHeight: 1 }}>{formatBRL(p.preco_venda)}</div>
          )}
        </div>
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 8 }}>
          {qtdAtual > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => onRemover(p.id)} style={{ width: 26, height: 26, borderRadius: '50%', background: '#1A1A2E', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
              <span style={{ fontWeight: 800, fontSize: '.875rem' }}>{fmtQtd(p, qtdAtual)}</span>
              <button onClick={() => onAdicionar(p)} style={{ width: 26, height: 26, borderRadius: '50%', background: '#FF6B35', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
            </div>
          ) : (
            <button onClick={() => { onAdicionar(p); toast.success('Adicionado!', { duration: 700 }); }}
              style={{ width: 30, height: 30, borderRadius: '50%', background: '#FF6B35', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Grid de Produtos ─────────────────────────────────────────
function ProdutoGrid({ produtos, isMobile, qtd, onAdicionar, onRemover, onAbrirModal, imageHeight }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(145px, 1fr))' : 'repeat(auto-fill, minmax(165px, 1fr))', gap: 14 }}>
      {produtos.map(p => (
        <ProdutoCard key={p.id} p={p} qtdAtual={qtd(p.id)} onAdicionar={onAdicionar} onRemover={onRemover} onAbrirModal={onAbrirModal} imageHeight={imageHeight} />
      ))}
    </div>
  );
}

// ── Card de Departamento (home) ──────────────────────────────
function DepartmentCard({ cat, isMobile, onVerMais, qtd, onAdicionar, onRemover, onAbrirModal }) {
  const mostrados = cat.produtos.slice(0, MAX_HOME);
  const temMais   = cat.produtos.length > MAX_HOME;

  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #eee', overflow: 'hidden', marginBottom: 24 }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
          {cat.descricao}
          <span style={{ fontSize: '.75rem', color: '#aaa', fontWeight: 500, marginLeft: 8 }}>({cat.produtos.length})</span>
        </h2>
        <button onClick={onVerMais} style={{ background: 'none', border: 'none', color: '#FF6B35', fontWeight: 700, cursor: 'pointer', fontSize: '.85rem', fontFamily: 'inherit' }}>
          Ver todos →
        </button>
      </div>
      <div style={{ padding: '16px 20px' }}>
        <ProdutoGrid produtos={mostrados} isMobile={isMobile} qtd={qtd} onAdicionar={onAdicionar} onRemover={onRemover} onAbrirModal={onAbrirModal} imageHeight={130} />
        {temMais && (
          <button onClick={onVerMais} style={{ marginTop: 16, width: '100%', background: '#fff8f5', border: '2px solid #FF6B35', borderRadius: 12, padding: '12px', color: '#FF6B35', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '.9rem' }}>
            Ver todos os {cat.produtos.length} produtos →
          </button>
        )}
      </div>
    </div>
  );
}

// ── Página Principal do Cardápio ─────────────────────────────
export default function Cardapio() {
  const { empresa_id } = useParams();
  const { itens, adicionar, remover, removerTudo, atualizarObservacao, total, totalItens, setEmpresaId } = useCarrinho();
  const { cliente, logoutCliente } = useClienteAuth();
  const navigate = useNavigate();

  const [empresa, setEmpresa]       = useState(null);
  const [cardapio, setCardapio]     = useState([]);
  const [lojaConfig, setLojaConfig] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [catAberta, setCatAberta]   = useState(null); // null = home
  const [subcatAtiva, setSubcat]    = useState(null);
  const [carrinhoAberto, setCarr]   = useState(false);
  const [busca, setBusca]           = useState('');
  const [prodModal, setProdModal]   = useState(null);
  const [contaMenu, setContaMenu]   = useState(false);
  const [isMobile, setIsMobile]     = useState(window.innerWidth < 768);
  const topRef = useRef(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    Promise.allSettled([
      api.get(`/cardapio/${empresa_id}`),
      api.get(`/loja/${empresa_id}/status`),
    ]).then(([cardapioRes, cfgRes]) => {
      if (cardapioRes.status === 'rejected') { toast.error('Cardápio não encontrado'); return; }
      setEmpresa(cardapioRes.value.data.empresa);
      setCardapio(cardapioRes.value.data.cardapio);
      setEmpresaId(empresa_id);
      if (cfgRes.status === 'fulfilled') setLojaConfig(cfgRes.value.data);
    }).finally(() => setLoading(false));
  }, [empresa_id]);

  const abrirCategoria = useCallback((catId) => {
    setCatAberta(catId);
    setSubcat(null);
    setBusca('');
    window.scrollTo({ top: 0 });
  }, []);

  const voltarHome = () => {
    setCatAberta(null);
    setSubcat(null);
    setBusca('');
    window.scrollTo({ top: 0 });
  };

  const produtosFiltrados = useCallback(() => {
    if (busca) {
      const b = busca.toLowerCase();
      return cardapio.map(c => ({
        ...c,
        produtos: c.produtos.filter(p =>
          p.descricao.toLowerCase().includes(b) ||
          (p.descricao_detalhada || '').toLowerCase().includes(b)
        ),
      })).filter(c => c.produtos.length > 0);
    }
    return cardapio;
  }, [cardapio, busca]);

  const qtd = (id) => itens.find(i => i.id === id)?.quantidade || 0;

  if (loading) return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F7F4' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 16 }}>🍽</div>
        <div style={{ width: 36, height: 36, border: '3px solid #eee', borderTopColor: '#FF6B35', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto' }} />
      </div>
    </div>
  );

  if (!empresa) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 20 }}>
      <div>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>😕</div>
        <h2>Cardápio não encontrado</h2>
        <p style={{ color: '#888', marginTop: 8 }}>Verifique o link e tente novamente.</p>
      </div>
    </div>
  );

  const isHome    = !catAberta && !busca;
  const isDept    = !!catAberta && !busca;
  const isSearch  = !!busca;
  const catDados  = cardapio.find(c => c.id === catAberta) || null;
  const prodsDept = isDept
    ? (subcatAtiva ? catDados.produtos.filter(p => p.subcategoria_id === subcatAtiva) : catDados?.produtos || [])
    : [];
  const resultsBusca = isSearch ? produtosFiltrados() : [];

  return (
    <div id="topo" ref={topRef} style={{ minHeight: '100dvh', background: '#F8F7F4', fontFamily: "'Sora', sans-serif" }}>

      {/* ── HEADER ── */}
      <header style={{ background: '#1A1A2E', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 16px rgba(0,0,0,.25)', paddingTop: 'env(safe-area-inset-top, 0px)' }}>

        {/* Linha 1: logo + nome + ações */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
          {empresa.logo_url
            ? <img src={`${API}${empresa.logo_url}`} alt="logo" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: '2px solid rgba(255,255,255,.15)' }} />
            : <div style={{ width: 44, height: 44, borderRadius: 10, background: '#FF6B35', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>🍽</div>
          }
          <div style={{ minWidth: 0 }}>
            <h1 style={{ color: '#fff', fontSize: '1rem', fontWeight: 800, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{empresa.nome_fantasia}</h1>
            {empresa.cidade && <div style={{ color: 'rgba(255,255,255,.5)', fontSize: '.72rem' }}>📍 {empresa.cidade}/{empresa.estado}</div>}
          </div>
          <div style={{ flex: 1 }} />
          {lojaConfig && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: lojaConfig.aberta ? '#22c55e' : '#ef4444', display: 'block' }} />
              <span style={{ color: lojaConfig.aberta ? '#22c55e' : '#ef4444', fontSize: '.75rem', fontWeight: 700 }}>
                {lojaConfig.aberta ? 'Aberta' : 'Fechada'}
              </span>
            </div>
          )}
          {empresa.telefone && (
            <a href={`https://wa.me/55${empresa.telefone.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer"
              style={{ background: '#25D366', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 12px', fontWeight: 700, fontSize: '.75rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              💬
            </a>
          )}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            {cliente ? (
              <button onClick={() => setContaMenu(v => !v)} title={`Olá, ${cliente.nome}`}
                style={{ background: '#FF6B35', border: 'none', borderRadius: 10, width: 38, height: 38, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '.85rem', fontFamily: 'inherit' }}>
                {cliente.nome.trim()[0].toUpperCase()}
              </button>
            ) : (
              <Link to={`/cliente/login?empresa_id=${empresa_id}&redirect=/${empresa_id}`}
                style={{ background: 'rgba(255,255,255,.12)', border: '1.5px solid rgba(255,255,255,.2)', borderRadius: 10, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', color: '#fff', fontSize: '1.1rem' }}>
                👤
              </Link>
            )}
            {contaMenu && cliente && (
              <>
                <div onClick={() => setContaMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 150 }} />
                <div style={{ position: 'absolute', top: 46, right: 0, zIndex: 151, background: '#fff', borderRadius: 12, minWidth: 180, boxShadow: '0 8px 32px rgba(0,0,0,.2)', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                    <div style={{ fontWeight: 700, fontSize: '.85rem', color: '#1A1A2E', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cliente.nome}</div>
                  </div>
                  <Link to="/cliente/conta" onClick={() => setContaMenu(false)} style={{ display: 'block', padding: '12px 16px', color: '#1A1A2E', textDecoration: 'none', fontSize: '.85rem', fontWeight: 600 }}>👤 Minha conta</Link>
                  <button onClick={() => { setContaMenu(false); logoutCliente(); toast.success('Você saiu da sua conta'); navigate(`/${empresa_id}`); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '12px 16px', background: 'none', border: 'none', borderTop: '1px solid #eee', color: '#ef4444', textAlign: 'left', cursor: 'pointer', fontSize: '.85rem', fontWeight: 600, fontFamily: 'inherit' }}>
                    🚪 Sair da conta
                  </button>
                </div>
              </>
            )}
          </div>
          <button onClick={() => setCarr(true)} style={{ background: '#FF6B35', border: 'none', borderRadius: 10, padding: '8px 14px', color: '#fff', cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, fontFamily: 'inherit' }}>
            🛒
            {totalItens > 0 && (
              <span style={{ background: '#fff', color: '#FF6B35', borderRadius: 99, fontSize: '.65rem', fontWeight: 900, padding: '1px 6px' }}>{totalItens}</span>
            )}
          </button>
        </div>

        {/* Linha 2: busca */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px 12px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>🔍</span>
            <input placeholder="Buscar produto..." value={busca}
              onChange={e => { setBusca(e.target.value); if (e.target.value) setCatAberta(null); }}
              style={{ width: '100%', background: 'rgba(255,255,255,.1)', border: '1.5px solid rgba(255,255,255,.15)', borderRadius: 12, padding: '10px 12px 10px 38px', color: '#fff', fontSize: '.875rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
            {busca && (
              <button onClick={() => setBusca('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
            )}
          </div>
        </div>

        {/* Linha 3: tabs de departamentos */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,.08)', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'flex', gap: 4, width: 'max-content' }}>
              {/* Tab Início */}
              <button onClick={voltarHome} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px', whiteSpace: 'nowrap', color: isHome ? '#FF6B35' : 'rgba(255,255,255,.6)', fontWeight: isHome ? 800 : 500, fontSize: '.82rem', fontFamily: 'inherit', borderBottom: isHome ? '3px solid #FF6B35' : '3px solid transparent', transition: 'all .15s', display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7A1 1 0 003 11h1v6a1 1 0 001 1h4v-4h2v4h4a1 1 0 001-1v-6h1a1 1 0 00.707-1.707l-7-7z" />
                </svg>
                Início
              </button>
              {cardapio.map(cat => (
                <button key={cat.id} onClick={() => abrirCategoria(cat.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px', whiteSpace: 'nowrap', color: catAberta === cat.id ? '#FF6B35' : 'rgba(255,255,255,.6)', fontWeight: catAberta === cat.id ? 800 : 500, fontSize: '.82rem', fontFamily: 'inherit', borderBottom: catAberta === cat.id ? '3px solid #FF6B35' : '3px solid transparent', transition: 'all .15s' }}>
                  {cat.descricao}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* ── BANNER LOJA FECHADA ── */}
      {lojaConfig && !lojaConfig.aberta && (
        <div style={{ background: '#1A1A2E', borderBottom: '3px solid #ef4444', padding: '12px 20px', textAlign: 'center' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>🔴</span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '.9rem' }}>Loja fechada no momento</span>
            {lojaConfig.mensagem && <span style={{ color: 'rgba(255,255,255,.6)', fontSize: '.82rem' }}>— {lojaConfig.mensagem}</span>}
          </div>
        </div>
      )}

      {/* ── CONTEÚDO PRINCIPAL ── */}
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px calc(100px + env(safe-area-inset-bottom, 0px))' }}>

        {/* ── HOME ── */}
        {isHome && (
          <>
            {/* Carrossel de banners */}
            {lojaConfig?.banners?.length > 0 && (
              <BannerCarrossel banners={lojaConfig.banners} />
            )}

            <div style={{ paddingTop: lojaConfig?.banners?.length ? 0 : 24 }}>
              {cardapio.map(cat => (
                <DepartmentCard
                  key={cat.id}
                  cat={cat}
                  isMobile={isMobile}
                  onVerMais={() => abrirCategoria(cat.id)}
                  qtd={qtd}
                  onAdicionar={adicionar}
                  onRemover={remover}
                  onAbrirModal={setProdModal}
                />
              ))}
              {cardapio.length === 0 && (
                <div style={{ textAlign: 'center', padding: '80px 20px', color: '#aaa' }}>
                  <div style={{ fontSize: '3rem', marginBottom: 12 }}>🍽</div>
                  <h3 style={{ color: '#555' }}>Nenhum produto disponível</h3>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── BUSCA ── */}
        {isSearch && (
          <div style={{ paddingTop: 24 }}>
            <div style={{ marginBottom: 16, color: '#888', fontSize: '.85rem' }}>
              {resultsBusca.reduce((a, c) => a + c.produtos.length, 0)} resultado(s) para "{busca}"
            </div>
            {resultsBusca.map(cat => (
              <div key={cat.id} style={{ marginBottom: 32 }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: 16, paddingBottom: 10, borderBottom: '2px solid #eee', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {cat.descricao}
                  <span style={{ fontSize: '.75rem', color: '#aaa', fontWeight: 500 }}>({cat.produtos.length})</span>
                </h2>
                <ProdutoGrid produtos={cat.produtos} isMobile={isMobile} qtd={qtd} onAdicionar={adicionar} onRemover={remover} onAbrirModal={setProdModal} imageHeight={150} />
              </div>
            ))}
            {resultsBusca.length === 0 && (
              <div style={{ textAlign: 'center', padding: '80px 20px', color: '#aaa' }}>
                <div style={{ fontSize: '3rem', marginBottom: 12 }}>🔍</div>
                <h3 style={{ color: '#555' }}>Nenhum produto encontrado</h3>
                <button onClick={() => setBusca('')} style={{ marginTop: 12, background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Limpar busca</button>
              </div>
            )}
          </div>
        )}

        {/* ── DEPARTAMENTO ── */}
        {isDept && catDados && (
          <div style={{ paddingTop: 20 }}>
            {/* Breadcrumb + voltar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: '.85rem', color: '#888' }}>
              <button onClick={voltarHome} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#FF6B35', fontWeight: 700, fontFamily: 'inherit', fontSize: '.85rem', padding: 0 }}>
                ← Início
              </button>
              <span>/</span>
              <span style={{ color: '#333', fontWeight: 600 }}>{catDados.descricao}</span>
            </div>

            {/* Chips de subcategoria no mobile */}
            {isMobile && (catDados.subcategorias || []).length > 0 && (
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 16, paddingBottom: 4, scrollbarWidth: 'none' }}>
                <button onClick={() => setSubcat(null)}
                  style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 99, border: `1.5px solid ${subcatAtiva === null ? '#FF6B35' : '#ddd'}`, background: subcatAtiva === null ? '#FF6B35' : '#fff', color: subcatAtiva === null ? '#fff' : '#555', fontWeight: 600, fontSize: '.8rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                  Todos
                </button>
                {catDados.subcategorias.map(sub => (
                  <button key={sub.id} onClick={() => setSubcat(sub.id)}
                    style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 99, border: `1.5px solid ${subcatAtiva === sub.id ? '#FF6B35' : '#ddd'}`, background: subcatAtiva === sub.id ? '#FF6B35' : '#fff', color: subcatAtiva === sub.id ? '#fff' : '#555', fontWeight: 600, fontSize: '.8rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                    {sub.descricao}
                  </button>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
              {/* Sidebar subcategorias (desktop) */}
              {!isMobile && (
                <SidebarSubcategorias
                  categoria={catDados}
                  subcatAtiva={subcatAtiva}
                  onSelectSubcat={setSubcat}
                />
              )}

              {/* Produtos */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <h2 style={{ fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
                    {subcatAtiva ? (catDados.subcategorias || []).find(s => s.id === subcatAtiva)?.descricao : 'Todos os produtos'}
                    <span style={{ fontSize: '.75rem', color: '#aaa', fontWeight: 500, marginLeft: 8 }}>({prodsDept.length})</span>
                  </h2>
                </div>

                {prodsDept.length > 0 ? (
                  <ProdutoGrid produtos={prodsDept} isMobile={isMobile} qtd={qtd} onAdicionar={adicionar} onRemover={remover} onAbrirModal={setProdModal} imageHeight={160} />
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 20px', color: '#aaa' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📦</div>
                    <h3 style={{ color: '#555' }}>Nenhum produto nesta subcategoria</h3>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </main>

      {/* ── RODAPÉ ── */}
      <Rodape empresa={empresa} />

      {/* ── CARRINHO FLUTUANTE ── */}
      <CarrinhoFlutuante total={total} totalItens={totalItens} onAbrir={() => setCarr(true)} />

      {/* ── CARRINHO DRAWER ── */}
      <CarrinhoDrawer aberto={carrinhoAberto} onFechar={() => setCarr(false)} itens={itens} total={total} totalItens={totalItens} onAdicionar={adicionar} onRemover={remover} onRemoverTudo={removerTudo} onAtualizarObservacao={atualizarObservacao} empresaId={empresa_id} />

      {/* ── MODAL PRODUTO ── */}
      <ProdutoModal produto={prodModal} onClose={() => setProdModal(null)} onAdicionar={adicionar} onRemover={remover}
        quantidade={prodModal ? qtd(prodModal.id) : 0}
        observacaoAtual={prodModal ? itens.find(i => i.id === prodModal.id)?.observacao : ''}
        onAtualizarObservacao={atualizarObservacao} />
    </div>
  );
}
